import {css, StyleSheet} from 'aphrodite';
import React, {ChangeEvent} from 'react';

import {TEXT_FIELD_BACKGROUND_COLOR, TEXT_FIELD_BORDER} from 'chemistry/molecules/MoleculeConstants';
import {MonomerCapGroup} from 'graphql-client/__generated/graphqlGlobalTypes';
import MonomerConfigureAttachmentPointsSection from 'molbio/monomers/admin/MonomerConfigureAttachmentPointsSection';
import {MISSING_CAPPING_GROUPS_MESSAGE, MonomerCapGroupWithWildcard} from 'molbio/monomers/MonomerConstants';
import {MonomerValidationErrors} from 'molbio/monomers/MonomersDAO';
import {FONT_PROXIMA_13, FONT_PROXIMA_18} from 'styles/FontConstants';
import {GRAY_DARKER} from 'ui-library/Colors';
import {InputErrorMessageStyle} from 'ui-library/inputs/InputConstants';
import TextField from 'ui-library/inputs/TextField';

interface MonomerManualCreationBuildPaneProps {
  smilesInput: string;
  onChangeSmilesInput: (smiles: string) => void;
  canonicalSmiles: string | null;
  attachmentPointsInput: Array<MonomerCapGroup | null> | null;
  onChangeAttachmentPointsInput: (attachmentPointsInput: Array<MonomerCapGroup | null> | null) => void;
  calculatedAttachmentPoints: Array<MonomerCapGroupWithWildcard> | null;
  validationErrors: MonomerValidationErrors;
}

export default function MonomerManualCreationBuildPane({
  smilesInput,
  onChangeSmilesInput,
  canonicalSmiles,
  attachmentPointsInput,
  onChangeAttachmentPointsInput,
  calculatedAttachmentPoints,
  validationErrors,
}: MonomerManualCreationBuildPaneProps): JSX.Element {
  // Don't show an error message if it's related to missing cap group selection
  const smilesInputFieldError =
    validationErrors.smiles ??
    (validationErrors.attachmentPoints !== MISSING_CAPPING_GROUPS_MESSAGE
      ? validationErrors.attachmentPoints
      : null);
  return (
    <div className={css(styles.paneContainer)}>
      <div className={css(styles.inputSmilesHeader)}>
        <span>Input SMILES</span>
      </div>
      <TextField
        className={css(styles.inputSmilesField)}
        value={smilesInput}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          onChangeSmilesInput(e.target.value);
        }}
        placeholder="Enter SMILES notation"
        autoFocus={true}
        hasError={smilesInputFieldError != null}
        error={
          smilesInputFieldError != null
            ? {
                message: smilesInputFieldError,
                style: InputErrorMessageStyle.INFO,
              }
            : null
        }
        dataTestAttrs={{
          component: 'MonomerManualCreationBuildPane',
          element: 'smilesInput',
        }}
      />
      <div>
        <div className={css(styles.textFieldSubheader)}>
          <span>Canonical SMILES</span>
        </div>
        <div className={css(styles.grayTextFieldContainer)}>
          <span className={css(styles.grayTextField)}>{canonicalSmiles}</span>
        </div>
      </div>
      <div>
        <div className={css(styles.attachmentPointsHeader)}>
          <span>Configure attachment points</span>
        </div>
        <div className={css(styles.attachmentPointsSubheader)}>
          <span>Assign capping groups for each attachment point</span>
        </div>
        <MonomerConfigureAttachmentPointsSection
          attachmentPointsInput={attachmentPointsInput}
          onChangeAttachmentPointsInput={onChangeAttachmentPointsInput}
          calculatedAttachmentPoints={calculatedAttachmentPoints}
        />
      </div>
    </div>
  );
}

const styles = StyleSheet.create({
  paneContainer: {
    marginLeft: 40,
    marginRight: 40,
  },
  inputSmilesHeader: {
    ...FONT_PROXIMA_18,
    marginTop: 18,
  },
  inputSmilesField: {
    marginBottom: 12,
  },
  textFieldSubheader: {
    fontWeight: 'bold',
    marginBottom: 4,
    ...FONT_PROXIMA_13,
  },
  flex1: {
    flex: 1,
  },
  grayTextFieldContainer: {
    display: 'flex',
  },
  grayTextField: {
    ...FONT_PROXIMA_13,
    padding: '3px 10px',
    background: TEXT_FIELD_BACKGROUND_COLOR,
    border: TEXT_FIELD_BORDER,
    boxSizing: 'border-box',
    borderRadius: '4px',
    overflowWrap: 'anywhere',
    minHeight: 28,
    width: '100%',
  },
  attachmentPointsHeader: {
    ...FONT_PROXIMA_18,
    marginTop: 32,
    marginBottom: 4,
  },
  attachmentPointsSubheader: {
    ...FONT_PROXIMA_13,
    color: GRAY_DARKER,
    marginBottom: 16,
  },
});
